import { useUser } from '@clerk/clerk-react';
import { CheckCircle2, Sparkles, Trophy } from 'lucide-react';
import { useEffect, useState } from 'react';
import Confetti from 'react-confetti';
import { useNavigate } from 'react-router-dom';
import { useWindowSize } from 'react-use';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Stage } from '@/types/stage.type';

interface StageCompletionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  stage: Stage;
  stageList: Stage[];
  completedItemsCount: number;
  totalItemsCount: number;
  brandName?: string;
}

export const StageCompletionDialog = ({
  open,
  onOpenChange,
  stage,
  stageList,
  completedItemsCount,
  totalItemsCount,
  brandName,
}: StageCompletionDialogProps) => {
  const { width, height } = useWindowSize();
  const [showConfetti, setShowConfetti] = useState(false);
  const navigate = useNavigate();
  const { user } = useUser();

  const getNextStage = () => {
    if (!stage.id || !stageList.length) return null;

    // Sort stages by index if available, otherwise use array order
    const sortedStages = [...stageList].sort((a, b) => {
      if (a.index !== undefined && b.index !== undefined) {
        return a.index - b.index;
      }
      return 0; // Keep original order if index not available
    });

    const currentStageIndex = sortedStages.findIndex((s) => s.id === stage.id);
    if (
      currentStageIndex === -1 ||
      currentStageIndex === sortedStages.length - 1
    ) {
      return null; // No next stage
    }

    return sortedStages[currentStageIndex + 1];
  };

  const handleContinue = () => {
    const nextStage = getNextStage();

    if (nextStage) {
      navigate(`/dashboard/discovery/${nextStage.id}`);
    } else {
      navigate('/dashboard');
    }

    onOpenChange(false);
  };

  const handleDialogClose = (open: boolean) => {
    if (!open) {
      // When dialog is closed (by X button or clicking outside), navigate to next stage
      const nextStage = getNextStage();

      if (nextStage) {
        navigate(`/dashboard/discovery/${nextStage.id}`);
      } else {
        navigate('/dashboard');
      }
    }

    onOpenChange(open);
  };

  useEffect(() => {
    if (open) {
      setShowConfetti(true);
      // Give enough time for confetti to fall naturally
      const timer = setTimeout(() => {
        setShowConfetti(false);
      }, 12000);

      return () => clearTimeout(timer);
    } else {
      setShowConfetti(false);
    }
  }, [open]);

  return (
    <>
      {showConfetti && (
        <Confetti
          width={width}
          height={height}
          recycle={false}
          numberOfPieces={150}
          gravity={0.1}
          initialVelocityX={3}
          initialVelocityY={8}
          style={{ zIndex: 9999 }}
        />
      )}
      <Dialog open={open} onOpenChange={handleDialogClose}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="sr-only">Stage Completed</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col items-center justify-center space-y-6 py-8">
            <div className="relative">
              <div className="bg-green-400/20 absolute -inset-4 animate-pulse rounded-full" />
              <div className="relative flex h-20 w-20 items-center justify-center rounded-full bg-green-500">
                <Trophy className="h-10 w-10 text-white" />
              </div>
            </div>

            <div className="space-y-2 text-center">
              <div className="flex items-center justify-center gap-2">
                <Sparkles className="h-5 w-5 text-green-600" />
                <h2 className="as-h4 as-strong text-zinc-900">
                  Congrats {user?.firstName || 'there'}! 🎉
                </h2>
                <Sparkles className="h-5 w-5 text-green-600" />
              </div>

              {getNextStage() ? (
                <div className="text-center">
                  <p className="as-body-03 text-zinc-600">
                    You've completed{' '}
                    <span className="as-strong">{stage.name}</span> and can now
                    move on to{' '}
                    <span className="as-strong">{getNextStage()?.name}</span>.
                  </p>
                  <p className="as-body-03 mt-2 text-zinc-600">
                    We're excited about your interest and hope to welcome you to
                    the{' '}
                    <span className="as-strong">
                      {brandName || 'franchise'}
                    </span>{' '}
                    franchisee community.
                  </p>
                </div>
              ) : (
                <div className="text-center">
                  <p className="as-body-03 text-zinc-600">
                    Thanks for completing{' '}
                    <span className="as-strong">{stage.name}</span>, which marks
                    the <span className="as-strong">successful completion</span>{' '}
                    of your journey to becoming a franchisee for{' '}
                    <span className="as-strong">
                      {brandName || 'this franchise'}
                    </span>
                    .
                  </p>
                  <p className="as-body-03 mt-2 text-zinc-600">
                    We're thrilled to{' '}
                    <span className="as-strong">
                      officially welcome you aboard
                    </span>
                    . Now it's time to{' '}
                    <span className="as-strong">kick off launch prep</span> and
                    get your location open and rolling!
                  </p>
                </div>
              )}
            </div>

            <div className="flex items-center gap-2 rounded-lg bg-green-50 px-4 py-2">
              <CheckCircle2 className="h-5 w-5 text-green-600" />
              <span className="as-title-03 as-strong text-green-800">
                {completedItemsCount} / {totalItemsCount} items completed
              </span>
            </div>

            <Button onClick={handleContinue} className="w-full">
              {getNextStage() ? 'Next Stage' : 'Go to Dashboard'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
